import {SelectField, type SelectFieldOption} from '@wandb/components';
import React, {useState} from 'react';
import type {Key} from 'react-aria-components';

import {useUnpatchTemplateFirstChild} from '../../util/hooks/useUnpatchTemplateFirstChild';
import type {Project} from './types';
import {useProjectPickerOptions} from './useProjectPickerOptions';

type ProjectPickerProps = {
  'aria-label'?: string;
  entityName?: string;
  exclude?: Project[];
  fullWidth?: boolean;
  matchTriggerWidth?: boolean;
  isDisabled?: boolean;
  label?: string;
  onSelectProject(project: Project | null): void;
  organizationId?: string;
  placeholder?: string;
  requireSameEntity?: boolean;
  selectedProject: Project | null;
};

const EMPTY_ARY = [] as Project[];

export function ProjectPicker({
  'aria-label': ariaLabel,
  entityName,
  exclude = EMPTY_ARY,
  fullWidth = false,
  matchTriggerWidth = false,
  isDisabled = false,
  label,
  onSelectProject,
  organizationId,
  placeholder = 'Select project',
  requireSameEntity = false,
  selectedProject,
}: ProjectPickerProps) {
  // ProjectPicker gets rendered into Reports, which triggers a bizarre bug
  // that is a combination of peculiar React behavior and React-Aria's attempt
  // to patch around that peculiar behavior _and_ contentEditable nodes. The
  // combination of factors causes React to die with a cryptic message,
  // and it permanently unhooks itself from the DOM. That's real bad.
  useUnpatchTemplateFirstChild();

  const [pattern, setPattern] = useState('');
  const {data, loading} = useProjectPickerOptions({
    entityName,
    exclude,
    organizationId,
    pattern,
    requireSameEntity,
    selectedProject,
  });

  const items: SelectFieldOption[] = data.map(d => ({
    id: d.internalId,
    label: `${d.entityName}/${d.name}`,
    decorationLeft: {iconProps: {icon: 'folder-project'}},
  }));

  const handleOnChange = (v: Key | null) => {
    if (!v) {
      onSelectProject(null);
    } else if (typeof v === 'string') {
      const selectedProject = data.find(d => d.internalId === v);
      onSelectProject(selectedProject ?? null);
    }
  };

  return (
    <SelectField
      allowsEmptyCollection
      aria-label={ariaLabel}
      fullWidth={fullWidth}
      matchTriggerWidth={matchTriggerWidth}
      isDisabled={isDisabled}
      isLoading={loading}
      items={items}
      value={selectedProject?.internalId}
      onChange={handleOnChange}
      label={label as any}
      placeholder={placeholder}
      search={{
        onInput: setPattern,
        value: pattern,
      }}
      startIcon="folder-project"
    />
  );
}
